import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { formatWithThousandSeparator } from '@/utils';

interface SignedAmountProps {
  /** raw amount — magnitude is always rendered, sign per `mode` */
  amount: number;
  /**
   * signed: +/− follows the amount's own sign (ledger rows).
   * debit: always renders −, i.e. a deduction line-item.
   * none: bare magnitude (totals that are facts, not movements).
   */
  mode?: 'signed' | 'debit' | 'none';
  className?: string;
  style?: CSSProperties;
}

/**
 * Signed money renderer (QA defect 6 — DESIGN §6 bidi rule). The sign +
 * digits + unit are one `<bdi dir='ltr'>` isolate, so an RTL context can
 * never re-order the sign to trail the number ("6,000-"). Zero amounts drop
 * the sign entirely (QA defect 12 — no "−0 IQD").
 */
function SignedAmount({ amount, mode = 'signed', className, style }: SignedAmountProps) {
  const { t } = useTranslation();
  const magnitude = Math.abs(amount);
  const sign =
    magnitude === 0 || mode === 'none' ? '' : mode === 'debit' || amount < 0 ? '−' : '+';

  return (
    <bdi dir='ltr' className={className} style={style}>
      {sign}
      {formatWithThousandSeparator(magnitude)} {String(t('iqd'))}
    </bdi>
  );
}

export default SignedAmount;
